import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { User, LogOut, ShoppingBag, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { customerApi, getCustomerToken, setCustomerToken, clearCustomerToken } from "@/lib/customer";
import { formatApiErrorDetail } from "@/lib/api";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const pretty = (s) => (s || "").replaceAll("_", " ");

export default function CustomerAccount({ primary = "#0a1a3f", customer, onAuthChange }) {
  const [authOpen, setAuthOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);

  // Restore session on mount
  useEffect(() => {
    if (getCustomerToken()) {
      customerApi.me().then((r) => onAuthChange(r.data)).catch(() => { clearCustomerToken(); onAuthChange(null); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(() => {
    customerApi.orders().then((r) => setOrders(r.data)).catch(() => {});
    customerApi.bookings().then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.email || !form.password) return toast.error("Email and password required");
    if (mode === "register" && !form.name) return toast.error("Please enter your name");
    setBusy(true);
    try {
      const { data } = mode === "register"
        ? await customerApi.register(form)
        : await customerApi.login({ email: form.email, password: form.password });
      setCustomerToken(data.token);
      onAuthChange(data.customer);
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "", phone: "" });
      toast.success(mode === "register" ? "Account created — welcome!" : "Signed in");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not sign in");
    } finally { setBusy(false); }
  };

  const logout = () => {
    clearCustomerToken();
    onAuthChange(null);
    setAccountOpen(false);
    toast.success("Signed out");
  };

  const openAccount = () => { setAccountOpen(true); loadData(); };

  return (
    <>
      {customer ? (
        <button data-testid="cust-account-btn" onClick={openAccount}
          className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 px-4 py-1.5 text-sm font-medium transition-colors">
          <User className="h-4 w-4" /> {customer.name?.split(" ")[0] || "Account"}
        </button>
      ) : (
        <button data-testid="cust-signin-btn" onClick={() => { setMode("login"); setAuthOpen(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-white/15 hover:bg-white/25 px-4 py-1.5 text-sm font-medium transition-colors">
          <User className="h-4 w-4" /> Sign in
        </button>
      )}

      {/* Auth dialog */}
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="max-w-sm" data-testid="cust-auth-dialog">
          <DialogHeader><DialogTitle className="font-display">{mode === "register" ? "Create account" : "Welcome back"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {mode === "register" && (
              <Input data-testid="cust-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
            )}
            <Input data-testid="cust-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" />
            <Input data-testid="cust-password" type="password" placeholder="Password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && submit()} className="h-11" />
            {mode === "register" && (
              <Input data-testid="cust-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" />
            )}
            <Button data-testid="cust-auth-submit" disabled={busy} onClick={submit} className="w-full h-11" style={{ background: primary }}>
              {busy ? "…" : mode === "register" ? "Create account" : "Sign in"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {mode === "register" ? "Already have an account? " : "New here? "}
              <button data-testid="cust-auth-toggle" className="font-medium underline" style={{ color: primary }}
                onClick={() => setMode(mode === "register" ? "login" : "register")}>
                {mode === "register" ? "Sign in" : "Create one"}
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account sheet */}
      <Sheet open={accountOpen} onOpenChange={setAccountOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="cust-account-sheet">
          <SheetHeader><SheetTitle className="font-display">My account</SheetTitle></SheetHeader>
          {customer && (
            <div className="mt-2">
              <p className="font-medium">{customer.name}</p>
              <p className="text-sm text-muted-foreground">{customer.email}</p>
            </div>
          )}

          <div className="mt-6">
            <h3 className="font-display font-bold flex items-center gap-2 mb-2"><ShoppingBag className="h-4 w-4" /> Orders</h3>
            {orders.length === 0 && <p className="text-sm text-muted-foreground" data-testid="cust-no-orders">No orders yet.</p>}
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} data-testid={`cust-order-${o.id}`} className="rounded-xl border border-stone-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{o.order_number}</span>
                    <span className="text-xs font-semibold rounded-full px-2 py-0.5" style={{ background: `${primary}15`, color: primary }}>{pretty(o.status)}</span>
                  </div>
                  {o.restaurant_name && <p className="text-xs text-muted-foreground">{o.restaurant_name}</p>}
                  <p className="text-sm mt-1">{(o.items || []).map((it) => `${it.quantity}× ${it.item_name_snapshot}`).join(", ")}</p>
                  <p className="text-sm font-semibold mt-1">{gbp(o.total)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-display font-bold flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4" /> Bookings</h3>
            {bookings.length === 0 && <p className="text-sm text-muted-foreground" data-testid="cust-no-bookings">No bookings yet.</p>}
            <div className="space-y-2">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`cust-booking-${b.id}`} className="rounded-xl border border-stone-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{b.booking_ref}</span>
                    <span className="text-xs font-semibold rounded-full px-2 py-0.5" style={{ background: `${primary}15`, color: primary }}>{pretty(b.status)}</span>
                  </div>
                  {b.restaurant_name && <p className="text-xs text-muted-foreground">{b.restaurant_name}</p>}
                  <p className="text-sm mt-1">{b.booking_date} at {b.booking_time} · {b.guest_count} guest(s)</p>
                </div>
              ))}
            </div>
          </div>

          <Button data-testid="cust-logout" variant="outline" onClick={logout} className="w-full h-11 mt-8 gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
}
